import React, { useEffect, useState } from "react";
import { api } from "../../lib/config";
import { AdminLayout } from "./AdminShell";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Trash2, Plus, Edit3, ShieldCheck, Lock } from "lucide-react";
import { ImageUploader, MultiImageUploader } from "../../components/ImageUploader";

export function AdminOverview({ user }) {
  const [counts, setCounts] = useState({ trips: 0, blogs: 0, testimonials: 0, enquiries: 0 });
  useEffect(() => {
    Promise.all([
      api.get("/trips", { params: { published_only: false } }),
      api.get("/blogs", { params: { published_only: false } }),
      api.get("/testimonials"),
      api.get("/enquiries"),
    ]).then(([t, b, ts, e]) => setCounts({ trips: t.data.length, blogs: b.data.length, testimonials: ts.data.length, enquiries: e.data.length }));
  }, []);
  return (
    <AdminLayout user={user} active="overview">
      <h1 className="font-display text-4xl text-owt-forest">Welcome back, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-owt-muted mt-2 font-mono text-[11px] tracking-[0.2em] uppercase">Studio at a glance</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
        {[
          ["Trips", counts.trips], ["Blogs", counts.blogs], ["Testimonials", counts.testimonials], ["Enquiries", counts.enquiries],
        ].map(([k, v]) => (
          <div key={k} className="border border-owt-border bg-owt-surface p-6" data-testid={`stat-${k.toLowerCase()}`}>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted">{k}</div>
            <div className="font-display text-5xl text-owt-forest mt-2">{v}</div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

const EMPTY_TRIP = {
  name: "", destination: "", country: "", start_date: "", end_date: "", duration_days: 7,
  price: 0, original_price: null, currency: "INR", seats_total: 12, seats_remaining: 12, difficulty: "Moderate", trip_type: "Group",
  hero_image: "", gallery: [], short_description: "", description: "",
  inclusions: [], exclusions: [], itinerary: [], faqs: [], featured: false, published: true, tags: [],
};

export function AdminTrips({ user }) {
  const [trips, setTrips] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/trips", { params: { published_only: false } }).then((r) => setTrips(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const payload = {
      ...edit,
      gallery: typeof edit.gallery === "string" ? edit.gallery.split("\n").filter(Boolean) : edit.gallery,
      inclusions: typeof edit.inclusions === "string" ? edit.inclusions.split("\n").filter(Boolean) : edit.inclusions,
      exclusions: typeof edit.exclusions === "string" ? edit.exclusions.split("\n").filter(Boolean) : edit.exclusions,
      tags: typeof edit.tags === "string" ? edit.tags.split(",").map((x)=>x.trim()).filter(Boolean) : edit.tags,
    };
    try {
      if (edit.id) await api.put(`/trips/${edit.id}`, payload);
      else await api.post("/trips", payload);
      toast.success("Saved");
      setEdit(null); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete trip?")) return; await api.delete(`/trips/${id}`); toast.success("Deleted"); load(); };

  return (
    <AdminLayout user={user} active="trips">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-owt-forest">Group Trips</h1>
        <button data-testid="admin-new-trip" onClick={()=>setEdit({ ...EMPTY_TRIP })} className="inline-flex items-center gap-2 bg-owt-forest text-owt-surface px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase"><Plus className="w-4 h-4"/> New Trip</button>
      </div>

      {edit ? (
        <div className="bg-owt-surface border border-owt-border p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input placeholder="Trip name" value={edit.name} onChange={(e)=>setEdit({...edit, name: e.target.value})}/>
            <Input placeholder="Destination" value={edit.destination} onChange={(e)=>setEdit({...edit, destination: e.target.value})}/>
            <Input placeholder="Country" value={edit.country} onChange={(e)=>setEdit({...edit, country: e.target.value})}/>
            <Input placeholder="Duration days" type="number" value={edit.duration_days} onChange={(e)=>setEdit({...edit, duration_days: Number(e.target.value)})}/>
            <Input placeholder="Start date (YYYY-MM-DD)" value={edit.start_date} onChange={(e)=>setEdit({...edit, start_date: e.target.value})}/>
            <Input placeholder="End date (YYYY-MM-DD)" value={edit.end_date} onChange={(e)=>setEdit({...edit, end_date: e.target.value})}/>
            <Input placeholder="Price" type="number" value={edit.price} onChange={(e)=>setEdit({...edit, price: Number(e.target.value)})}/>
            <Input placeholder="Original price (strikethrough, optional)" type="number" value={edit.original_price || ""} onChange={(e)=>setEdit({...edit, original_price: e.target.value ? Number(e.target.value) : null})}/>
            <Input placeholder="Seats total" type="number" value={edit.seats_total} onChange={(e)=>setEdit({...edit, seats_total: Number(e.target.value)})}/>
            <Input placeholder="Seats remaining" type="number" value={edit.seats_remaining} onChange={(e)=>setEdit({...edit, seats_remaining: Number(e.target.value)})}/>
            <Input placeholder="Difficulty (Easy/Moderate/Challenging)" value={edit.difficulty} onChange={(e)=>setEdit({...edit, difficulty: e.target.value})}/>
            <select value={edit.trip_type || "Group"} onChange={(e)=>setEdit({...edit, trip_type: e.target.value})} className="border border-owt-border px-3 py-2 text-sm bg-owt-surface" data-testid="admin-trip-type">
              <option value="Group">Group trip</option>
              <option value="Private">Private trip</option>
            </select>
          </div>
          <ImageUploader label="Hero image" value={edit.hero_image} onChange={(url)=>setEdit({...edit, hero_image: url})}/>
          <Textarea placeholder="Short description" value={edit.short_description} onChange={(e)=>setEdit({...edit, short_description: e.target.value})}/>
          <Textarea placeholder="Description" rows={4} value={edit.description} onChange={(e)=>setEdit({...edit, description: e.target.value})}/>
          <MultiImageUploader label="Gallery photos" value={Array.isArray(edit.gallery) ? edit.gallery : (edit.gallery ? String(edit.gallery).split("\n").filter(Boolean) : [])} onChange={(arr)=>setEdit({...edit, gallery: arr})}/>
          <Textarea placeholder="Inclusions (one per line)" rows={3} value={Array.isArray(edit.inclusions) ? edit.inclusions.join("\n") : edit.inclusions} onChange={(e)=>setEdit({...edit, inclusions: e.target.value})}/>
          <Textarea placeholder="Exclusions (one per line)" rows={3} value={Array.isArray(edit.exclusions) ? edit.exclusions.join("\n") : edit.exclusions} onChange={(e)=>setEdit({...edit, exclusions: e.target.value})}/>
          <Input placeholder="Tags (comma separated)" value={Array.isArray(edit.tags) ? edit.tags.join(", ") : edit.tags} onChange={(e)=>setEdit({...edit, tags: e.target.value})}/>

          {/* Itinerary editor */}
          <div className="border-t border-owt-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted">Day-by-day itinerary</div>
              <button type="button" onClick={()=>setEdit({...edit, itinerary: [...(edit.itinerary || []), { day: (edit.itinerary?.length || 0) + 1, title: "", description: "", images: [] }]})} className="text-owt-terracotta font-mono text-[10px] tracking-[0.2em] uppercase" data-testid="add-day">+ Add Day</button>
            </div>
            <div className="space-y-3">
              {(edit.itinerary || []).map((d, i) => (
                <div key={i} className="border border-owt-border p-3 bg-owt-bg/50 space-y-2" data-testid={`edit-day-${d.day}`}>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Day #" type="number" value={d.day} onChange={(e)=>{ const it=[...edit.itinerary]; it[i]={...it[i], day: Number(e.target.value)}; setEdit({...edit, itinerary: it}); }} className="w-20"/>
                    <Input placeholder="Title" value={d.title} onChange={(e)=>{ const it=[...edit.itinerary]; it[i]={...it[i], title: e.target.value}; setEdit({...edit, itinerary: it}); }} className="flex-1"/>
                    <button type="button" onClick={()=>{ const it=edit.itinerary.filter((_, x)=>x!==i); setEdit({...edit, itinerary: it}); }} className="text-owt-terracotta"><Trash2 className="w-4 h-4"/></button>
                  </div>
                  <Textarea placeholder="Description" rows={2} value={d.description} onChange={(e)=>{ const it=[...edit.itinerary]; it[i]={...it[i], description: e.target.value}; setEdit({...edit, itinerary: it}); }}/>
                  <MultiImageUploader label="Day photos" value={Array.isArray(d.images) ? d.images : []} onChange={(arr)=>{ const it=[...edit.itinerary]; it[i]={...it[i], images: arr}; setEdit({...edit, itinerary: it}); }}/>
                  <Input placeholder="Hotel / stay name (private trips)" value={d.hotel_name || ""} onChange={(e)=>{ const it=[...edit.itinerary]; it[i]={...it[i], hotel_name: e.target.value}; setEdit({...edit, itinerary: it}); }}/>
                  <MultiImageUploader label="Hotel photos (private trips)" value={Array.isArray(d.hotel_images) ? d.hotel_images : []} onChange={(arr)=>{ const it=[...edit.itinerary]; it[i]={...it[i], hotel_images: arr}; setEdit({...edit, itinerary: it}); }}/>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ editor */}
          <div className="border-t border-owt-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted">FAQs</div>
              <button type="button" onClick={()=>setEdit({...edit, faqs: [...(edit.faqs || []), { q: "", a: "" }]})} className="text-owt-terracotta font-mono text-[10px] tracking-[0.2em] uppercase" data-testid="add-faq">+ Add FAQ</button>
            </div>
            <div className="space-y-3">
              {(edit.faqs || []).map((f, i) => (
                <div key={i} className="border border-owt-border p-3 bg-owt-bg/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input placeholder="Question" value={f.q} onChange={(e)=>{ const arr=[...edit.faqs]; arr[i]={...arr[i], q: e.target.value}; setEdit({...edit, faqs: arr}); }} className="flex-1"/>
                    <button type="button" onClick={()=>{ const arr=edit.faqs.filter((_, x)=>x!==i); setEdit({...edit, faqs: arr}); }} className="text-owt-terracotta"><Trash2 className="w-4 h-4"/></button>
                  </div>
                  <Textarea placeholder="Answer" rows={2} value={f.a} onChange={(e)=>{ const arr=[...edit.faqs]; arr[i]={...arr[i], a: e.target.value}; setEdit({...edit, faqs: arr}); }}/>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase"><Switch checked={edit.featured} onCheckedChange={(v)=>setEdit({...edit, featured: v})}/> Featured</label>
            <label className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase"><Switch checked={edit.published} onCheckedChange={(v)=>setEdit({...edit, published: v})}/> Published</label>
          </div>
          <div className="flex gap-3">
            <button onClick={save} data-testid="admin-trip-save" className="bg-owt-forest text-owt-surface px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">Save</button>
            <button onClick={()=>setEdit(null)} className="border border-owt-border px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {trips.map((t) => (
            <div key={t.id} className="flex items-center gap-4 bg-owt-surface border border-owt-border p-4" data-testid={`admin-trip-${t.id}`}>
              <img src={t.hero_image} alt="" className="w-20 h-20 object-cover"/>
              <div className="flex-1">
                <div className="font-display text-xl text-owt-forest">{t.name}</div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted">{t.country} · {t.seats_remaining}/{t.seats_total} seats · {t.featured ? "Featured · " : ""}{t.published ? "Published" : "Draft"}</div>
              </div>
              <button onClick={()=>setEdit(t)} className="text-owt-forest hover:text-owt-terracotta"><Edit3 className="w-4 h-4"/></button>
              <button onClick={()=>del(t.id)} className="text-owt-terracotta"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

const EMPTY_BLOG = { title: "", slug: "", excerpt: "", cover_image: "", content_html: "", category: "destination-guides", read_time: 5, author: "Once Way", published: true, tags: [] };
export function AdminBlogs({ user }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/blogs", { params: { published_only: false } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const payload = { ...edit, tags: typeof edit.tags === "string" ? edit.tags.split(",").map((x)=>x.trim()).filter(Boolean) : edit.tags };
    try {
      if (edit.id) await api.put(`/blogs/${edit.id}`, payload); else await api.post("/blogs", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/blogs/${id}`); load(); };

  return (
    <AdminLayout user={user} active="blogs">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-owt-forest">Journal</h1>
        <button data-testid="admin-new-blog" onClick={()=>setEdit({...EMPTY_BLOG})} className="bg-owt-forest text-owt-surface px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase inline-flex items-center gap-2"><Plus className="w-4 h-4"/> New Post</button>
      </div>
      {edit ? (
        <div className="bg-owt-surface border border-owt-border p-6 space-y-4">
          <Input placeholder="Title" value={edit.title} onChange={(e)=>setEdit({...edit, title: e.target.value})}/>
          <Input placeholder="Slug (kebab-case)" value={edit.slug} onChange={(e)=>setEdit({...edit, slug: e.target.value})}/>
          <Input placeholder="Category (destination-guides|travel-tips|trip-recaps)" value={edit.category} onChange={(e)=>setEdit({...edit, category: e.target.value})}/>
          <ImageUploader label="Cover image" value={edit.cover_image} onChange={(url)=>setEdit({...edit, cover_image: url})}/>
          <Input placeholder="Author" value={edit.author} onChange={(e)=>setEdit({...edit, author: e.target.value})}/>
          <Input placeholder="Read time (min)" type="number" value={edit.read_time} onChange={(e)=>setEdit({...edit, read_time: Number(e.target.value)})}/>
          <Textarea placeholder="Excerpt" value={edit.excerpt} onChange={(e)=>setEdit({...edit, excerpt: e.target.value})}/>
          <Textarea placeholder="HTML content (supports <p> <h2> <img src=..> etc.)" rows={10} value={edit.content_html} onChange={(e)=>setEdit({...edit, content_html: e.target.value})}/>
          <Input placeholder="Tags (comma separated)" value={Array.isArray(edit.tags) ? edit.tags.join(", ") : edit.tags} onChange={(e)=>setEdit({...edit, tags: e.target.value})}/>
          <label className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase"><Switch checked={edit.published} onCheckedChange={(v)=>setEdit({...edit, published: v})}/> Published</label>
          <div className="flex gap-3">
            <button onClick={save} data-testid="admin-blog-save" className="bg-owt-forest text-owt-surface px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">Save</button>
            <button onClick={()=>setEdit(null)} className="border border-owt-border px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((b) => (
            <div key={b.id} className="flex items-center gap-4 bg-owt-surface border border-owt-border p-4">
              <img src={b.cover_image} alt="" className="w-20 h-20 object-cover"/>
              <div className="flex-1">
                <div className="font-display text-xl text-owt-forest">{b.title}</div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted">{b.category} · {b.published ? "Published" : "Draft"}</div>
              </div>
              <button onClick={()=>setEdit(b)}><Edit3 className="w-4 h-4"/></button>
              <button onClick={()=>del(b.id)} className="text-owt-terracotta"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

const EMPTY_TST = { author: "", trip: "", quote: "", avatar: "", rating: 5, published: true };
export function AdminTestimonials({ user }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/testimonials/${edit.id}`, edit); else await api.post("/testimonials", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/testimonials/${id}`); load(); };
  return (
    <AdminLayout user={user} active="testimonials">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-owt-forest">Testimonials</h1>
        <button onClick={()=>setEdit({...EMPTY_TST})} className="bg-owt-forest text-owt-surface px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase inline-flex items-center gap-2"><Plus className="w-4 h-4"/> New</button>
      </div>
      {edit ? (
        <div className="bg-owt-surface border border-owt-border p-6 space-y-3">
          <Input placeholder="Author" value={edit.author} onChange={(e)=>setEdit({...edit, author: e.target.value})}/>
          <Input placeholder="Trip name" value={edit.trip} onChange={(e)=>setEdit({...edit, trip: e.target.value})}/>
          <Textarea placeholder="Quote" value={edit.quote} onChange={(e)=>setEdit({...edit, quote: e.target.value})}/>
          <ImageUploader label="Avatar (optional)" value={edit.avatar || ""} onChange={(url)=>setEdit({...edit, avatar: url})}/>
          <div className="flex gap-3"><button onClick={save} className="bg-owt-forest text-owt-surface px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">Save</button><button onClick={()=>setEdit(null)} className="border border-owt-border px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">Cancel</button></div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((t) => (
            <div key={t.id} className="flex items-center gap-4 bg-owt-surface border border-owt-border p-4">
              <div className="flex-1">
                <div className="font-display text-lg text-owt-forest">{t.author}</div>
                <div className="text-owt-muted italic">"{t.quote.slice(0, 100)}..."</div>
              </div>
              <button onClick={()=>setEdit(t)}><Edit3 className="w-4 h-4"/></button>
              <button onClick={()=>del(t.id)} className="text-owt-terracotta"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminEnquiries({ user }) {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  useEffect(() => { api.get("/enquiries", { params: type ? { type } : {} }).then((r)=>setItems(r.data)); }, [type]);
  return (
    <AdminLayout user={user} active="enquiries">
      <h1 className="font-display text-4xl text-owt-forest mb-6">Enquiries</h1>
      <div className="flex gap-2 mb-6">
        {["", "private-trip", "contact", "booking"].map((t) => (
          <button key={t} onClick={()=>setType(t)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border ${type === t ? "bg-owt-forest text-owt-surface" : "border-owt-border"}`}>{t || "all"}</button>
        ))}
      </div>
      <div className="grid gap-3">
        {items.map((e) => (
          <div key={e.id} className="bg-owt-surface border border-owt-border p-4">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-terracotta">{e.type}</div>
              <div className="font-mono text-[10px] text-owt-muted">{new Date(e.created_at).toLocaleString()}</div>
            </div>
            <pre className="mt-2 whitespace-pre-wrap text-sm text-owt-ink font-sans">{JSON.stringify(e, null, 2)}</pre>
          </div>
        ))}
        {items.length === 0 && <div className="text-owt-muted font-mono text-sm">No enquiries yet.</div>}
      </div>
    </AdminLayout>
  );
}

export function AdminAccess({ user }) {
  const [data, setData] = useState({ protected: [], extra: [] });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/allowed-emails").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/allowed-emails", { email: email.trim() });
      toast.success("Email added — they can now sign in");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add email");
    } finally { setBusy(false); }
  };
  const remove = async (em) => {
    if (!window.confirm(`Remove ${em}? They will lose admin access immediately.`)) return;
    try {
      await api.delete(`/admin/allowed-emails/${encodeURIComponent(em)}`);
      toast.success("Access removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove email");
    }
  };
  return (
    <AdminLayout user={user} active="access">
      <h1 className="font-display text-4xl text-owt-forest">Admin Access</h1>
      <p className="text-owt-muted mt-2 font-mono text-[11px] tracking-[0.2em] uppercase">Only these emails can sign in to the console</p>

      <form onSubmit={add} className="mt-8 flex gap-3 max-w-lg">
        <Input type="email" placeholder="new.admin@gmail.com" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="access-email-input"/>
        <button type="submit" disabled={busy} className="bg-owt-forest text-owt-surface px-5 font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-2" data-testid="access-add-btn">
          <Plus className="w-3 h-3"/> Allow
        </button>
      </form>

      <div className="mt-8 max-w-lg divide-y divide-owt-border border border-owt-border bg-owt-surface" data-testid="access-email-list">
        {data.protected.map((em) => (
          <div key={em} className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-4 h-4 text-owt-forest"/>
              <span className="text-sm text-owt-ink">{em}</span>
            </div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted"><Lock className="w-3 h-3"/> Founder</span>
          </div>
        ))}
        {data.extra.map((row) => (
          <div key={row.email} className="flex items-center justify-between px-4 py-3" data-testid={`access-row-${row.email}`}>
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-4 h-4 text-owt-terracotta"/>
              <span className="text-sm text-owt-ink">{row.email}</span>
            </div>
            <button onClick={()=>remove(row.email)} className="text-owt-muted hover:text-owt-terracotta" data-testid={`access-remove-${row.email}`}><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </div>

      <p className="mt-6 max-w-lg text-sm text-owt-muted leading-relaxed">
        Anyone signing in with an email not on this list is blocked and you receive an alert email at info@oncewaytravel.com. Founder emails are protected and cannot be removed.
      </p>
    </AdminLayout>
  );
}
